import {
  ChatAPI,
  getCurrentChat,
  setBotTyping,
  setFlowStep,
  setRunningStep,
} from '@guuru/chat-web';
import log from '@guuru/logger-web';
import { Sentry } from '@guuru/sentry-web';
import postHeightUpdate from '../commands/postHeightUpdate';
import booting from './booting';
import chatEnd from './chatEnd';
import chatInit from './chatInit';
import chatLoad from './chatLoad';
import chatQuestion from './chatQuestion';
import chatStart from './chatStart';
import connectingExpert from './connectingExpert';
import greeting from './greeting';
import makePrediction from './makePrediction';
import organizationSelection from './organizationSelection';
import quickActionPredict from './quickActionPredict';
import quickActionSelection from './quickActionSelection';
import requestCategory from './requestCategory';
import requestEmail from './requestEmail';
import requestIntentSelection from './requestIntentSelection';
import requestName from './requestName';
import requestVisitorRole from './requestVisitorRole';
import showWelcomeMessage from './showWelcomeMessage';

// Each step needs to implement 3 functions

// - validate: should be as lightheight as possible and only validate if
// it needs to call the run function.

// - render: is responsible to display the action to the user
// it can return some context that will change the nextStep
// * goToNextStep - continue the normal flow,
// use when step doesn't need to wait for response
// * goToStep - ability to skip some steps
// * nextStepOptions - pass some context to the next step only

// - processResponse: is responsible to gather any input given by the user
// and save it, its called right before the next action
// it can return some context that will change next step
// * stayInCurrentStep - block flow (currently used by quickActions in
// direct link)
// * goToStep - ability to skip some steps
// * nextStepOptions - pass some context to the next step only

const stepActions = {
  organizationSelection,
  chatInit,
  chatLoad,
  chatStart,
  chatEnd,
  quickActionSelection,
  showWelcomeMessage,
  chatQuestion,
  greeting,
  requestIntentSelection,
  makePrediction,
  requestCategory,
  requestVisitorRole,
  requestName,
  requestEmail,
  connectingExpert,
  booting,
  quickActionPredict,
};

const chooseStepToRun = async (partnerId, nextStep, options = {}) => {
  const canRun = await stepActions[nextStep]?.validate?.(partnerId, options);
  let runResult = {};
  if (canRun) {
    setRunningStep(true);
    setFlowStep(nextStep);
    try {
      runResult = await stepActions[nextStep].render(partnerId, options);
      postHeightUpdate(nextStep);
    } finally {
      setRunningStep(false);
    }
  }

  if (!canRun || runResult?.goToNextStep) {
    const { flowSteps: chatFlowSteps } = getCurrentChat();
    const next = chatFlowSteps[chatFlowSteps.indexOf(nextStep) + 1];
    await chooseStepToRun(partnerId, next, {
      ...options,
      ...runResult?.nextStepOptions,
    });
    return;
  }

  const { goToStep, nextStepOptions } = runResult || {};
  if (goToStep) {
    await chooseStepToRun(partnerId, goToStep, {
      ...options,
      ...nextStepOptions,
    });
  }
};

const runNextStep = async ({ processArgs, action } = {}) => {
  try {
    const { flowStep: currentStep, flowSteps: chatFlowSteps } =
      getCurrentChat();

    const partnerId = ChatAPI.storeRetrievePartnerId();

    setBotTyping(true);

    if (!currentStep) {
      await chooseStepToRun(partnerId, chatFlowSteps[0], { action });
      return;
    }

    const { stayInCurrentStep, goToStep, nextStepOptions } =
      (await stepActions[currentStep]?.processResponse?.(
        partnerId,
        processArgs,
      )) || {};

    if (stayInCurrentStep) {
      postHeightUpdate(currentStep);
      return;
    }

    const nextStep =
      goToStep || chatFlowSteps[chatFlowSteps.indexOf(currentStep) + 1];

    Sentry.addBreadcrumb({
      category: 'chat.steps',
      data: { nextStep, currentStep },
    });

    await chooseStepToRun(partnerId, nextStep, {
      ...nextStepOptions,
      action,
      skipAutomation: processArgs?.skipAutomation,
    });
  } catch (error) {
    log.error(error);
  } finally {
    setBotTyping(false);
  }
};

export default runNextStep;
